// Version checking utility for forcing browser refresh on new deployments

interface VersionInfo {
  version: string;
  buildTime: string;
}

class VersionChecker {
  private static instance: VersionChecker;
  private currentVersion: string | null = null;
  private currentBuildTime: string | null = null;
  private checkInterval: NodeJS.Timeout | null = null;
  private isChecking = false;
  private isInitialized = false;
  private lastReloadTime: number = 0;
  private readonly RELOAD_COOLDOWN = 60000; // 1 minute cooldown between reloads

  static getInstance(): VersionChecker {
    if (!VersionChecker.instance) {
      VersionChecker.instance = new VersionChecker();
    }
    return VersionChecker.instance;
  }  // Initialize version checking
  async init(): Promise<void> {
    // Prevent multiple initializations
    if (this.isInitialized) {
      console.log('[VersionChecker] Already initialized, skipping...');
      return;
    }
    
    try {
      // Get current version and build time, store them (don't update later)
      const versionInfo = await this.fetchVersionInfoFromServer();
      this.currentVersion = versionInfo.version;
      this.currentBuildTime = versionInfo.buildTime;
      this.isInitialized = true;
      console.log('[VersionChecker] Initialized with version:', this.currentVersion, 'build time:', this.currentBuildTime);
      
      // Do NOT start periodic checking automatically - only manual checks
      console.log('[VersionChecker] Periodic checking disabled - manual checks only');
    } catch (error) {
      console.error('[VersionChecker] Initialization failed:', error);
    }
  }
  // Get version info from version.json (for initialization only)
  private async fetchVersionInfoFromServer(): Promise<VersionInfo> {
    try {
      const response = await fetch('/version.json?t=' + Date.now(), {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch version info');
      }
      
      const versionInfo: VersionInfo = await response.json();
      return versionInfo;
    } catch (error) {
      console.error('[VersionChecker] Failed to get current version:', error);
      // Fallback to package.json version or timestamp
      return {
        version: Date.now().toString(),
        buildTime: new Date().toISOString()
      };
    }
  }  // Check if a new version is available
  private async checkForNewVersion(): Promise<boolean> {
    if (this.isChecking || !this.currentVersion) {
      return false;
    }

    this.isChecking = true;
    
    try {
      const latestVersionInfo = await this.fetchVersionInfoFromServer();
      const latestVersion = latestVersionInfo.version;
      
      if (latestVersion !== this.currentVersion) {
        console.log('[VersionChecker] New version detected:', latestVersion, 'Current:', this.currentVersion);
        return true;
      }
      
      console.log('[VersionChecker] Already on latest version:', this.currentVersion);
      return false;
    } catch (error) {
      console.error('[VersionChecker] Version check failed:', error);
      return false;
    } finally {
      this.isChecking = false;
    }
  }  // Start periodic version checking
  private startPeriodicCheck(intervalMs: number = 1800000): void { // Check every 30 minutes (reduced frequency)
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    this.checkInterval = setInterval(async () => {
      // Only check if not already checking and if tab is visible
      if (!this.isChecking && !document.hidden) {
        const hasNewVersion = await this.checkForNewVersion();
        
        if (hasNewVersion) {
          this.handleNewVersionDetected();
        }
      }
    }, intervalMs);
  }  // Handle new version detection (manual only)
  private handleNewVersionDetected(): void {
    // Check reload cooldown to prevent excessive reloads
    const now = Date.now();
    if (now - this.lastReloadTime < this.RELOAD_COOLDOWN) {
      console.log('[VersionChecker] Reload skipped due to cooldown');
      return;
    }
    
    // This method is now only used for manual checks
    // No automatic dispatching of events or reloads
    console.log('[VersionChecker] New version detected - manual check required');
  }

  // Show update notification to user (legacy - keeping for compatibility)
  private showUpdateNotification(): void {
    // This method is now deprecated in favor of custom events
    // UI components should listen for 'newVersionAvailable' event instead
    console.log('[VersionChecker] New version available - use event listeners for UI notifications');
  }

  // Force reload the application
  private forceReload(): void {
    // Clear all caches
    if ('caches' in window) {
      caches.keys().then(cacheNames => {
        cacheNames.forEach(cacheName => {
          caches.delete(cacheName);
        });
      });
    }

    // Clear service worker cache and reload
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          registration.unregister();
        });
        
        // Force hard reload after service worker cleanup
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      });
    } else {
      // Force hard reload
      window.location.reload();
    }
  }  // Get version info (for UI display)
  getVersionInfo(): { currentVersion: string | null } {
    return {
      currentVersion: this.currentVersion
    };
  }
  // Get current version from server (public method for UI)
  async getCurrentVersion(): Promise<string> {
    const versionInfo = await this.fetchVersionInfoFromServer();
    return versionInfo.version;
  }
  // Manual version check (can be called from UI)
  async checkNow(): Promise<boolean> {
    const hasNewVersion = await this.checkForNewVersion();
    return hasNewVersion;
  }
  // Manual version check with reload (for UI components)
  async checkNowAndReload(): Promise<boolean> {
    const hasNewVersion = await this.checkForNewVersion();
    
    if (hasNewVersion) {
      // Check reload cooldown to prevent excessive reloads
      const now = Date.now();
      if (now - this.lastReloadTime < this.RELOAD_COOLDOWN) {
        console.log('[VersionChecker] Reload skipped due to cooldown');
        return false; // Don't reload if within cooldown period
      }
      
      this.lastReloadTime = now;
      
      // Give the UI component a chance to show feedback before reloading
      setTimeout(() => {
        this.forceReload();
      }, 2000); // 2 second delay to show the snackbar
      return true;
    }
    
    return false;
  }

  // Stop version checking
  stop(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }
}

export default VersionChecker;
